import React, { useMemo, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { finalReportApi, userPanelApi } from 'api';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'components/_new/Button';
import { usePanel } from 'contexts/panelContext';
import { classNames } from 'primereact/utils';
import { Page } from 'components/_new/Page';
import { Spinner } from 'components/_new/Spinner';
import { usePair } from 'contexts/pairContext';
import { MessageBox } from 'components/_new/MessageBox';
import { ArrowList } from 'components/_new/List';
import { FinalSurveyPanel } from 'components/finalSurveyPage/FinalSurveyPanel';
import { ContentSection } from 'components/_new/Content/ContentSection';
import { surveyApi } from 'api/survey';
import { DownloadMaterialsButton } from './DownloadMaterialsButton';
import PairPartnerInfo from '../components/PairPartnerInfo';
import { LinkDocArticleWithIconWrapper } from '../sessions/components/LinkDocArticleWithIconWrapper';

export const ProcessSummary = () => {
	const { t } = useTranslation();
	const { organizationName, programName, type } = useParams();
	const { currentApplicationData, currentProgramMembership } = usePanel();
	const {
		pair: { id: pairId },
	} = usePair();

	const membershipId = Number(currentProgramMembership.id);

	const {
		mutate: prepareDownloadLinkForSelfMutate,
		data: downloadLinkForSelf,
		isLoading: prepareDownloadLinkForSelfLoading,
	} = useMutation(finalReportApi.prepareFinalReportDownloadLinkForSelf);

	const {
		mutate: prepareDownloadLinkForMenteeMutate,
		data: downloadLinkForMentee,
		isLoading: prepareDownloadLinkForMenteeLoading,
	} = useMutation(finalReportApi.prepareFinalReportDownloadLinkForOther);

	useEffect(() => {
		prepareDownloadLinkForSelfMutate({ pairId, membershipId, type: String(type) });
		if (type === 'mentor') {
			prepareDownloadLinkForMenteeMutate({ pairId, membershipId, byRole: String(type), type: 'mentee' });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [membershipId, pairId, type]);

	const navigate = useNavigate();

	const { refetch: finishProcess } = useQuery(
		['finishProcess'],
		() => userPanelApi.finishProcessForPair(pairId, currentProgramMembership.id),
		{
			onSuccess: () => {
				navigate(
					currentApplicationData.applicationRole === 'mentee'
						? `/${organizationName}/${programName}/apply-mentee`
						: `/${organizationName}/${programName}/`
				);
				setTimeout(() => {
					window.location.reload();
				}, 100);
			},
		}
	);
	const {
		data: finalReportData,
		isLoading: finalReportLoading,
		refetch: finalReportRefetch,
	} = useQuery(['final-report', pairId], () => finalReportApi.getFinalReport(pairId, currentApplicationData?.id), {
		enabled: Boolean(pairId),
		refetchOnWindowFocus: false,
		onSuccess: () => {},
	});

	const [finalSurveyDialogOpen, setFinalSurveyDialogOpen] = useState(false);
	const { data, isLoading: finalSurveyLoading } = useQuery(
		['FINAL_SURVEY', { programMembershipId: Number(currentProgramMembership?.id) }],
		() => surveyApi.getSurveyForProgramMembership(Number(currentProgramMembership?.id)),
		{
			enabled: Boolean(currentProgramMembership),
		}
	);
	const hasAnswers = (data?.stats?.questionFilledCount || 0) > 0;

	return (
		<>
			<Helmet title={t('userPanel.navigation.processSummary')} />
			<SubPageTitle
				title={
					<div className="flex flex-column">
						{t('userPanel.navigation.processSummary')}
						<PairPartnerInfo />
					</div>
				}
			/>
			<Page>
				<ContentSection title={t('userPanel.processSummary.thanks')} titleIconProps={{ name: 'circle-check' }}>
					<MessageBox variant="pink">{t('userPanel.processSummary.desc')}</MessageBox>
				</ContentSection>

				<ContentSection title={t('userPanel.processSummary.whatNext')} titleIconProps={{ name: 'list-ol' }}>
					<ArrowList additionalElementMargin className="sm:pl-3">
						<li>
							{type === 'mentor' && (
								<Trans
									t={t}
									i18nKey="userPanel.processSummary.readInKnowledgeForMentor"
									components={[<LinkDocArticleWithIconWrapper articleLabel="end-process-mentor" />]}
								/>
							)}
							{type === 'mentee' && (
								<Trans
									t={t}
									i18nKey="userPanel.processSummary.readInKnowledgeForMentee"
									components={[<LinkDocArticleWithIconWrapper articleLabel="end-process-mentee" />]}
								/>
							)}
						</li>
						<li>
							<div>
								{type === 'mentor'
									? t('userPanel.processSummary.downloadReportForMentor')
									: t('userPanel.processSummary.downloadReportForMentee')}
							</div>
							{finalReportLoading ? <Spinner size="sm" /> : null}
							{type === 'mentor' && !finalReportData?.visibleForMentee && !finalReportLoading && (
								<MessageBox
									variant="rose"
									message={
										<Trans
											t={t}
											i18nKey="userPanel.processSummary.reportNotFilledForMentor"
											components={[
												<Link
													to={`/panel/${organizationName}/${programName}/${type}/pair/${pairId}/final-report`}
													className="underline"
												/>,
											]}
										/>
									}
									highlight
								/>
							)}
							{!finalReportLoading && (
								<div className="flex flex-col gap-2 mt-4 justify-content-center align-items-center w-full">
									<Button
										label={t('userPanel.processSummary.downloadReportForYou')}
										variant="primary"
										loading={prepareDownloadLinkForSelfLoading}
										onClick={() => {
											window.open(downloadLinkForSelf, '_blank');
										}}
									/>
									{type === 'mentor' && (
										<Button
											label={t('userPanel.processSummary.downloadReportForMentee')}
											variant="primary"
											loading={prepareDownloadLinkForMenteeLoading}
											onClick={() => {
												window.open(downloadLinkForMentee, '_blank');
											}}
										/>
									)}
								</div>
							)}
						</li>
						<li>
							<div className={classNames({ 'mb-4': !hasAnswers })}>
								{t('userPanel.processSummary.pleaseRateAndFillSurvey')}
							</div>

							{finalSurveyLoading ? (
								<Spinner center size="sm" />
							) : (
								<>
									{hasAnswers && (
										<>
											<MessageBox
												variant="success"
												message={t('userPanel.processSummary.survey.thanks')}
												highlight
											/>
											<Button
												label={t(
													'userPanel.processSummary.survey.openFinalSurveyAndCheckAnswers'
												)}
												onClick={() => {
													setFinalSurveyDialogOpen(true);
												}}
												className="px-auto py-2"
											/>
										</>
									)}

									{!hasAnswers && (
										<>
											<MessageBox
												variant="rose"
												message={t('userPanel.processSummary.survey.fillFinalSurvey')}
												highlight
											/>
											<Button
												label={t('userPanel.processSummary.survey.openFinalSurvey')}
												onClick={() => {
													setFinalSurveyDialogOpen(true);
												}}
												className="px-auto py-2"
											/>
										</>
									)}
								</>
							)}

							<FinalSurveyPanel
								visible={finalSurveyDialogOpen}
								onHide={() => setFinalSurveyDialogOpen(false)}
							/>
						</li>
						<li>
							<Trans
								t={t}
								i18nKey="userPanel.processSummary.followUsOnLinkedIn"
								components={[
									// eslint-disable-next-line jsx-a11y/control-has-associated-label, jsx-a11y/anchor-has-content
									<a
										href="https://www.linkedin.com/company/mentiway/"
										rel="noreferrer"
										target="_blank"
										className="p-link"
									/>,
								]}
							/>
						</li>
						<li>
							<div className="mb-3">{t('userPanel.processSummary.downloadAllSessionMaterials')}</div>
							<DownloadMaterialsButton withInfo />
						</li>
					</ArrowList>
				</ContentSection>
			</Page>
		</>
	);
};

export default ProcessSummary;
